import { Link2, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { EntitySelect } from '@/components/EntitySelect';
import type { ApiError } from '@/core/api';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { WORK_ITEM_RELATION_KINDS, type WorkItemRelationKind } from '../constants';
import { workItemRelationsHooks } from '../queries';
import { makeWorkItemSearch } from '../select';
import type { WorkItem, WorkItemBrief, WorkItemRelation } from '../types';

/** Kind label as seen from `currentId`'s perspective, and the "other" item.
 *  A relation is stored directionally (from → to); if the current item is on the
 *  `to` side we invert blocks⇄blocked_by so it reads correctly. */
const INVERSE: Record<WorkItemRelationKind, WorkItemRelationKind> = {
  blocks: 'blocked_by',
  blocked_by: 'blocks',
  relates_to: 'relates_to',
  duplicate: 'duplicate',
};
const LABEL: Record<WorkItemRelationKind, string> = Object.fromEntries(
  WORK_ITEM_RELATION_KINDS.map((k) => [k.value, k.label]),
) as Record<WorkItemRelationKind, string>;

function perspective(rel: WorkItemRelation, currentId: number): {
  kind: WorkItemRelationKind;
  other: WorkItemBrief | null;
} {
  if (rel.from_item === currentId) return { kind: rel.kind, other: rel.to_item_detail };
  return { kind: INVERSE[rel.kind], other: rel.from_item_detail };
}

export function WorkItemRelations({ item, project }: { item: WorkItem; project: { id: number } }) {
  const list = workItemRelationsHooks.useList({ item: item.id });
  const create = workItemRelationsHooks.useCreate();
  const remove = workItemRelationsHooks.useRemove();

  const [kind, setKind] = useState<WorkItemRelationKind>('blocked_by');
  // The shared create hook only toasts non-field errors, so a rejected pick
  // (e.g. `to_item`: different project) is shown here instead.
  const [error, setError] = useState<string | null>(null);
  const search = useMemo(
    () => makeWorkItemSearch(project.id, { excludeId: item.id }),
    [project.id, item.id],
  );

  const relations: WorkItemRelation[] = Array.isArray(list.data)
    ? list.data
    : (list.data?.results ?? []);

  // Pick a work item → add the relation immediately (no separate button).
  function add(target: string) {
    if (!target || create.isPending) return;
    setError(null);
    create.mutate(
      { from_item: item.id, to_item: Number(target), kind },
      {
        // Non-field errors are already toasted by the hook.
        onError: (err: ApiError) => {
          if (err.fieldErrors) setError(Object.values(err.fieldErrors).flat()[0] ?? err.message);
        },
      },
    );
  }

  return (
    <div>
      <h3 className="mb-2 flex items-center gap-2 text-sm font-medium">
        <Link2 className="text-muted-foreground size-4" /> Relations
      </h3>

      {relations.length ? (
        <ul className="mb-3 space-y-1.5">
          {relations.map((rel) => {
            const { kind: k, other } = perspective(rel, item.id);
            return (
              <li key={rel.id} className="group flex items-center gap-2 text-sm">
                <span className="text-muted-foreground w-24 shrink-0">{LABEL[k]}</span>
                {other ? (
                  <Link
                    to={`/projects/${other.project_public_id}/work-items/${other.identifier || other.public_id}`}
                    className="hover:text-primary min-w-0 truncate underline-offset-2 hover:underline"
                  >
                    <span className="text-muted-foreground">{other.identifier}</span> {other.title}
                  </Link>
                ) : (
                  <span className="text-muted-foreground">(removed)</span>
                )}
                <Button
                  size="icon"
                  variant="ghost"
                  className="ml-auto size-6 shrink-0 opacity-0 transition-opacity group-hover:opacity-100"
                  aria-label="Remove relation"
                  onClick={() => remove.mutate(rel.id)}
                >
                  <X className="size-3.5" />
                </Button>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="text-muted-foreground mb-3 text-sm">No relations.</p>
      )}

      <div className="flex items-center gap-2">
        <Select value={kind} onValueChange={(v) => setKind(v as WorkItemRelationKind)}>
          <SelectTrigger className="w-32 shrink-0" aria-label="Relation type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {WORK_ITEM_RELATION_KINDS.map((k) => (
              <SelectItem key={k.value} value={k.value}>
                {k.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="min-w-0 flex-1">
          <EntitySelect
            queryKey={`wi-relation-${item.id}`}
            value=""
            onChange={add}
            search={search}
            placeholder="Add a work item…"
          />
        </div>
      </div>
      {error ? (
        <p role="alert" className="text-destructive mt-1.5 text-sm">
          {error}
        </p>
      ) : null}
    </div>
  );
}
